import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import {
  Phone, Plus, Trash2, Search, PhoneCall, Loader2, AlertTriangle, AlertCircle,
  PhoneOutgoing, Settings, Bell, MessageSquare, Save, RotateCcw,
  Info, Clock, ArrowRightLeft, Check, ArrowRight, Briefcase, Building, X,
  MessageCircle, RefreshCw, CalendarClock, ChevronDown, ChevronUp, FileCheck, Eye, ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import { formatPhoneInput } from "../../utils/phoneUtils";
import PhoneConfigModal from "./PhoneConfigModal";
import LimitWarningModal from "../LimitWarningModal";
import VoiceSettingsCard from "./VoiceSettingsCard";
import CallRoutingCard from "./CallRoutingCard";
import { CaComplianceStep, CaBundleStatusCard } from "./CaComplianceForm";

const ConversationMaintenanceCard = ({ API, getAuthConfig }) => {
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  const handleMerge = async () => {
    setMerging(true);
    setMergeResult(null);
    try {
      const res = await axios.post(`${API}/sms/admin/merge-duplicate-conversations`, {}, getAuthConfig());
      setMergeResult({ success: true, merged: res.data.conversations_merged || 0, messages: res.data.messages_moved || 0 });
      if (res.data.conversations_merged > 0) toast.success(`Merged ${res.data.conversations_merged} duplicate conversations!`);
      else toast.info("No duplicate conversations found - all good!");
    } catch (error) {
      setMergeResult({ success: false, error: error.response?.data?.detail || "Failed to merge conversations" });
      toast.error("Failed to merge conversations");
    } finally { setMerging(false); }
  };

  return (
    <Card className="glass-card border-theme mt-4" data-testid="conversation-maintenance-card">
      <CardHeader>
        <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2"><Settings className="w-5 h-5" /> Conversation Maintenance</CardTitle>
        <CardDescription className="text-muted-foreground">Fix messaging issues like duplicate conversations or threading problems</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 rounded-xl bg-theme-surface border border-theme">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h4 className="text-foreground font-medium mb-1">Merge Duplicate Conversations</h4>
              <p className="text-muted-foreground text-sm">If you see multiple conversations for the same phone number, this will merge them into one thread.</p>
            </div>
            <Button onClick={handleMerge} disabled={merging} className="btn-primary whitespace-nowrap" data-testid="merge-conversations-btn">
              {merging ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Merging...</> : <><Settings className="w-4 h-4 mr-2" />Merge Conversations</>}
            </Button>
          </div>
          {mergeResult && (
            <div className={`mt-4 p-3 rounded-lg ${mergeResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
              {mergeResult.success ? (
                <div className="flex items-center gap-2 text-green-400"><Check className="w-4 h-4" /><span className="text-sm">{mergeResult.merged > 0 ? `Successfully merged ${mergeResult.merged} conversations and moved ${mergeResult.messages} messages` : "No duplicates found - your conversations are already organized correctly"}</span></div>
              ) : (
                <div className="flex items-center gap-2 text-red-400"><AlertTriangle className="w-4 h-4" /><span className="text-sm">{mergeResult.error}</span></div>
              )}
            </div>
          )}
        </div>
        <div className="text-muted-foreground text-xs"><p>Tip: Run this after seeing duplicate conversations in the Messages page.</p></div>
      </CardContent>
    </Card>
  );
};

const PhoneManagementTab = ({
  phoneNumbers, jobSources, limits, isDark, isMobileApp,
  phoneLimits, atPhoneLimit, nearPhoneLimit,
  smsSettings, setSmsSettings, smsSettingsLoading, saveSmsSettings, resetSmsTemplate,
  techReminderSettings, setTechReminderSettings, techReminderLoading, saveTechReminderSettings, resetTechReminderTemplate,
  businessSettings, confirmDelete, fetchAllSettings,
  API, getAuthConfig, refetchLimits,
  showPhoneLimitModal, setShowPhoneLimitModal,
}) => {
  // Local phone management state
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneConfigOpen, setPhoneConfigOpen] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchAreaCode, setSearchAreaCode] = useState("415");
  const [searchProvince, setSearchProvince] = useState("any");
  const [searchCountry, setSearchCountry] = useState("US");
  const [searchNumberType, setSearchNumberType] = useState("all");
  const [searchHint, setSearchHint] = useState("");
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [purchaseStep, setPurchaseStep] = useState(1);
  const [purchaseForm, setPurchaseForm] = useState({ phone_number: "", friendly_name: "", label: "", assignment_type: "business", assignment_id: "" });
  const [caProfile, setCaProfile] = useState(null);

  const isCA = searchCountry === "CA";
  const configureStep = isCA ? 3 : 2;
  const confirmStep = isCA ? 4 : 3;
  const purchaseSteps = isCA
    ? [{ n: 1, label: "Search" }, { n: 2, label: "Regulatory" }, { n: 3, label: "Configure" }, { n: 4, label: "Confirm" }]
    : [{ n: 1, label: "Search" }, { n: 2, label: "Configure" }, { n: 3, label: "Confirm" }];

  // Transfer state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferStep, setTransferStep] = useState("check");
  const [transferPhone, setTransferPhone] = useState("");
  const [checkingPortability, setCheckingPortability] = useState(false);
  const [portabilityResult, setPortabilityResult] = useState(null);
  const [transferForm, setTransferForm] = useState({
    account_number: "", account_pin: "", authorized_name: "", current_carrier: "", label: "",
    contact_email: "",
    billing_street: "", billing_city: "", billing_state: "", billing_zip: "",
  });
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  const [transferRequests, setTransferRequests] = useState([]);
  const [moveErrorDetail, setMoveErrorDetail] = useState("");
  const [moveConfirmed, setMoveConfirmed] = useState(false);
  const [moveStatus, setMoveStatus] = useState(null); // null | checking | ready | moving | configuring | completed | failed
  const [refreshingRequest, setRefreshingRequest] = useState(null);
  const [verifyingNumbers, setVerifyingNumbers] = useState(false);

  const handleVerifyNumbers = async () => {
    setVerifyingNumbers(true);
    try {
      const res = await axios.post(`${API}/phone/numbers/verify`, {}, getAuthConfig());
      const d = res.data || {};
      const notOwned = d.not_owned || [];
      const fixed = d.webhooks_fixed || [];
      if (notOwned.length > 0) {
        toast.error(
          `${notOwned.length} number(s) are NOT on your Twilio account — calls to them will fail: ` +
          notOwned.map(n => `${n.phone_number}${n.label ? ` (${n.label})` : ""}`).join(", "),
          { duration: 15000 }
        );
      }
      if (fixed.length > 0) {
        toast.success(`Repaired call webhooks on ${fixed.length} number(s): ${fixed.map(f => f.phone_number).join(", ")}`, { duration: 10000 });
      }
      if (notOwned.length === 0 && fixed.length === 0) {
        toast.success(`All ${d.checked} number(s) verified — everything is connected correctly`);
      }
      fetchAllSettings && fetchAllSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setVerifyingNumbers(false);
    }
  };
  const [expandedRequest, setExpandedRequest] = useState(null);

  // Fetch transfer requests on mount
  React.useEffect(() => {
    axios.get(`${API}/settings/phone-numbers/transfer-requests`, getAuthConfig()).then(res => setTransferRequests(res.data || [])).catch(() => {});
  }, []);

  // Load any saved Canadian regulatory profile when the purchase dialog opens
  React.useEffect(() => {
    if (!phoneDialogOpen) return;
    axios.get(`${API}/settings/ca-compliance`, getAuthConfig()).then(res => setCaProfile(res.data?.profile || null)).catch(() => {});
  }, [phoneDialogOpen]);

  const refreshTransferRequest = async (requestId) => {
    setRefreshingRequest(requestId);
    try {
      const res = await axios.post(`${API}/settings/phone-numbers/transfer-requests/${requestId}/refresh`, {}, getAuthConfig());
      setTransferRequests(prev => prev.map(r => r.request_id === requestId ? res.data : r));
      toast.success("Status refreshed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh status");
    } finally {
      setRefreshingRequest(null);
    }
  };

  // The plan limit is a FREE-TIER threshold, not a hard cap. Over-limit
  // numbers are flagged as overage server-side and billed accordingly, so
  // the Buy / Transfer Number buttons must always remain clickable.
  // `atPhoneLimit` is repurposed to drive the overage notice UI (banner +
  // tooltip), not to disable controls.

  const handleBuyNumberClick = () => {
    setPurchaseStep(1);
    setPurchaseForm({ phone_number: "", friendly_name: "", label: "", assignment_type: "business", assignment_id: "" });
    setAvailableNumbers([]); setSearchNumberType("all"); setSearchHint("");
    setPhoneDialogOpen(true);
  };

  const searchAvailableNumbers = async () => {
    setSearchingNumbers(true); setSearchHint("");
    try {
      const params = new URLSearchParams({ area_code: searchAreaCode, number_type: searchNumberType, country: searchCountry });
      if (searchCountry === "CA" && searchProvince !== "any" && !searchAreaCode) params.set("region", searchProvince);
      const res = await axios.get(`${API}/settings/phone-numbers/available?${params}`, getAuthConfig());
      setAvailableNumbers(res.data.numbers || []);
      if (res.data.hint) setSearchHint(res.data.hint);
      if (!res.data.twilio_configured) toast.info("Twilio not configured - showing sample numbers");
    } catch { toast.error("Failed to search numbers"); }
    finally { setSearchingNumbers(false); }
  };

  const purchaseNumber = async (number) => {
    try {
      await axios.post(`${API}/settings/phone-numbers/purchase`, { phone_number: number.phone_number, label: purchaseForm.label || "Main Line", forward_to: purchaseForm.forward_to, country: searchCountry }, getAuthConfig());
      toast.success("Phone number purchased");
      setPhoneDialogOpen(false);
      fetchAllSettings();
      refetchLimits();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to purchase number", { duration: 8000 }); }
  };

  const resetTransferDialog = () => {
    setTransferStep("check"); setTransferPhone(""); setPortabilityResult(null);
    setTransferForm({ account_number: "", account_pin: "", authorized_name: "", current_carrier: "", label: "", contact_email: "", billing_street: "", billing_city: "", billing_state: "", billing_zip: "" });
    setMoveConfirmed(false); setMoveStatus(null);
  };

  const handleTransferClick = () => {
    resetTransferDialog(); setTransferDialogOpen(true);
  };

  const checkPortability = async () => {
    if (!transferPhone.trim()) return;
    setCheckingPortability(true); setPortabilityResult(null);
    try {
      const res = await axios.post(`${API}/settings/phone-numbers/check-portability`, { phone_number: transferPhone }, getAuthConfig());
      setPortabilityResult(res.data);
      if (res.data.is_twilio_number) {
        // Twilio number detected → route to the Move flow
        setTransferStep("twilio_move");
        setMoveStatus("ready");
      } else if (res.data.portable) {
        setTransferStep("form");
      } else if (res.data.already_in_account) {
        setTransferStep("connect");
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to check portability"); }
    finally { setCheckingPortability(false); }
  };

  const registerExistingNumber = async () => {
    setSubmittingTransfer(true);
    try {
      const res = await axios.post(`${API}/settings/phone-numbers/register-existing`, {
        phone_number: portabilityResult.phone_number,
        twilio_sid: portabilityResult.twilio_sid,
        label: transferForm.label || "Transferred Line",
        assignment_type: "business"
      }, getAuthConfig());
      toast.success(res.data.message || "Number connected successfully!");
      setTransferStep("submitted");
      setTransferDialogOpen(false);
      fetchAllSettings();
      refetchLimits();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to connect number"); }
    finally { setSubmittingTransfer(false); }
  };

  const moveTwilioNumber = async () => {
    if (!moveConfirmed) { toast.error("Please confirm you control this number"); return; }
    if (!transferForm.account_number?.trim()) { toast.error("Source Account SID is required"); return; }
    if (!transferForm.account_pin?.trim()) { toast.error("Source PIN / Auth Token is required"); return; }
    setMoveStatus("moving");
    try {
      setMoveStatus("configuring");
      const res = await axios.post(`${API}/settings/phone-numbers/move-twilio`, {
        phone_number: portabilityResult?.phone_number || transferPhone,
        label: transferForm.label || "Moved Twilio Line",
        confirmed: true,
        source_account_sid: transferForm.account_number.trim(),
        source_pin: transferForm.account_pin.trim(),
      }, getAuthConfig());
      setMoveStatus("completed");
      toast.success(res.data.message || "Number moved successfully!");
      setTimeout(() => {
        setTransferDialogOpen(false);
        fetchAllSettings();
        refetchLimits();
      }, 1500);
    } catch (error) {
      setMoveStatus("failed");
      const detail = error.response?.data?.detail || "Failed to move number";
      setMoveErrorDetail(detail);
      toast.error(detail);
    }
  };

  const submitTransferRequest = async () => {
    if (!(transferForm.authorized_name || "").trim()) { toast.error("Authorized name is required"); return; }
    if (!(transferForm.contact_email || "").trim()) { toast.error("Contact email is required"); return; }
    setSubmittingTransfer(true);
    try {
      const payload = {
        phone_number: transferPhone,
        ...transferForm,
        billing_address: (transferForm.billing_street || transferForm.billing_city) ? {
          street: transferForm.billing_street,
          city: transferForm.billing_city,
          state: transferForm.billing_state,
          zip: transferForm.billing_zip,
          country: "US",
        } : undefined,
      };
      // Remove flat billing fields from payload
      delete payload.billing_street;
      delete payload.billing_city;
      delete payload.billing_state;
      delete payload.billing_zip;
      const res = await axios.post(`${API}/settings/phone-numbers/transfer`, payload, getAuthConfig());
      toast.success(res.data.message || "Transfer request submitted!");
      setTransferStep("submitted");
      const reqRes = await axios.get(`${API}/settings/phone-numbers/transfer-requests`, getAuthConfig());
      setTransferRequests(reqRes.data || []);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to submit transfer request"); }
    finally { setSubmittingTransfer(false); }
  };

  const cancelTransferRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/settings/phone-numbers/transfer-requests/${requestId}`, getAuthConfig());
      toast.success("Transfer request cancelled");
      const res = await axios.get(`${API}/settings/phone-numbers/transfer-requests`, getAuthConfig());
      setTransferRequests(res.data || []);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to cancel request"); }
  };

  return (
    <>
      <LimitWarningModal open={showPhoneLimitModal} onClose={() => setShowPhoneLimitModal(false)} title="Phone Line Limit Reached" limitType="dedicated_lines" current={phoneLimits.used} limit={phoneLimits.limit} planName={limits?.plan || "Current"} />

      <Card className="glass-card border-theme" data-testid="phone-numbers-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <CardTitle className="text-foreground font-['Manrope']">Virtual Phone Numbers</CardTitle>
              {!phoneLimits.unlimited && (
                <Badge
                  className={
                    atPhoneLimit
                      ? "bg-yellow-500/20 text-yellow-400"
                      : nearPhoneLimit
                      ? "bg-yellow-500/20 text-yellow-400"
                      : "bg-theme-hover text-muted-foreground"
                  }
                  data-testid="phone-lines-badge"
                >
                  {phoneLimits.used} / {phoneLimits.limit} included
                </Badge>
              )}
            </div>
            <CardDescription className="text-muted-foreground">Purchase and manage business phone numbers with call routing</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {!isMobileApp && (
              <Button variant="outline" className="border-theme text-foreground hover:bg-theme-hover" data-testid="verify-numbers-btn"
                disabled={verifyingNumbers} onClick={handleVerifyNumbers}>
                <ShieldCheck className="w-4 h-4 mr-2" /> {verifyingNumbers ? "Verifying..." : "Verify Numbers"}
              </Button>
            )}
            {!isMobileApp && (
              <Button variant="outline" className="border-theme text-foreground hover:bg-theme-hover" data-testid="transfer-number-btn" onClick={handleTransferClick}>
                <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Number
              </Button>
            )}
            <Dialog open={phoneDialogOpen} onOpenChange={setPhoneDialogOpen}>
              {!isMobileApp && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button className="btn-primary" data-testid="buy-number-btn" onClick={handleBuyNumberClick}>
                          <Plus className="w-4 h-4 mr-2" /> Buy Number
                          {atPhoneLimit && !phoneLimits.unlimited && <AlertTriangle className="w-4 h-4 ml-2 text-yellow-400" />}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {atPhoneLimit && !phoneLimits.unlimited && (
                      <TooltipContent className="bg-popover border-border text-popover-foreground">
                        <p>You've used your {phoneLimits.limit} included numbers. Additional numbers are billed as overage.</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              <DialogContent className="bg-background border-border sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="purchase-dialog">
                <DialogHeader className="pb-2">
                  <DialogTitle className="text-foreground flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#FFCC00]/20 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-[#FFCC00]" />
                    </div>
                    {purchaseStep === 1 ? "Find a Number" : isCA && purchaseStep === 2 ? "Canadian Regulatory Info" : purchaseStep === configureStep ? "Configure Number" : "Confirm Purchase"}
                  </DialogTitle>
                </DialogHeader>

                {/* Stepper */}
                <div className="flex items-center gap-1 mb-4" data-testid="purchase-stepper">
                  {purchaseSteps.map((s, i) => (
                    <React.Fragment key={s.n}>
                      {i > 0 && <div className={`flex-1 h-px ${purchaseStep >= s.n ? 'bg-[#FFCC00]' : isDark ? 'bg-white/10' : 'bg-gray-200'}`} />}
                      <div className="flex items-center gap-1.5">
                        <div className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center transition-colors ${
                          purchaseStep > s.n ? 'bg-[#FFCC00] text-black' : purchaseStep === s.n ? 'bg-[#FFCC00]/20 text-[#FFCC00] ring-2 ring-[#FFCC00]/40' : isDark ? 'bg-white/10 text-muted-foreground' : 'bg-gray-100 text-gray-400'
                        }`}>{purchaseStep > s.n ? <Check className="w-3 h-3" /> : s.n}</div>
                        <span className={`text-xs hidden sm:inline ${purchaseStep === s.n ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s.label}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {/* Step 1: Search */}
                {purchaseStep === 1 && (
                  <PurchaseStep1
                    searchAreaCode={searchAreaCode} setSearchAreaCode={setSearchAreaCode}
                    searchProvince={searchProvince} setSearchProvince={setSearchProvince}
                    searchCountry={searchCountry} setSearchCountry={setSearchCountry}
                    searchNumberType={searchNumberType} setSearchNumberType={setSearchNumberType}
                    searchingNumbers={searchingNumbers} searchAvailableNumbers={searchAvailableNumbers}
                    availableNumbers={availableNumbers} setAvailableNumbers={setAvailableNumbers}
                    purchaseForm={purchaseForm} setPurchaseForm={setPurchaseForm}
                    searchHint={searchHint} setSearchHint={setSearchHint}
                    setPurchaseStep={setPurchaseStep} isDark={isDark}
                  />
                )}

                {/* Step 2 (Canada only): Regulatory compliance info */}
                {isCA && purchaseStep === 2 && (
                  <CaComplianceStep
                    API={API} getAuthConfig={getAuthConfig}
                    caProfile={caProfile}
                    onSaved={(p) => { setCaProfile(p); setPurchaseStep(3); }}
                    onBack={() => setPurchaseStep(1)}
                    isDark={isDark}
                  />
                )}

                {/* Configure */}
                {purchaseStep === configureStep && (
                  <PurchaseStep2
                    purchaseForm={purchaseForm} setPurchaseForm={setPurchaseForm}
                    setPurchaseStep={setPurchaseStep} jobSources={jobSources} isDark={isDark}
                    backStep={isCA ? 2 : 1} nextStep={confirmStep}
                  />
                )}

                {/* Confirm */}
                {purchaseStep === confirmStep && (
                  <PurchaseStep3
                    purchaseForm={purchaseForm} jobSources={jobSources}
                    setPurchaseStep={setPurchaseStep} purchaseNumber={purchaseNumber} isDark={isDark}
                    backStep={configureStep} caProfile={isCA ? caProfile : null}
                  />
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Overage notice — appears once the user has met or exceeded
              their plan's included number count. The Buy/Transfer buttons
              stay enabled; the additional lines are billed as overage. */}
          {atPhoneLimit && !phoneLimits.unlimited && (
            <div
              className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/5"
              data-testid="phone-overage-notice"
            >
              <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-foreground">
                  You've used all {phoneLimits.limit} included number{phoneLimits.limit === 1 ? "" : "s"}.
                </p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  Additional numbers will be charged at the overage rate.
                </p>
              </div>
            </div>
          )}
          {phoneNumbers.length > 0 ? (
            <div className="space-y-3">
              {phoneNumbers.map((phone, i) => (
                <PhoneNumberCard key={phone.number_id} phone={phone} index={i} isDark={isDark}
                  onSettings={() => { setEditingPhone(phone); setPhoneConfigOpen(true); }}
                  onDelete={() => confirmDelete("phone", phone.number_id, phone.phone_number)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Phone className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No phone numbers configured</p>
              <p className="text-muted-foreground text-sm mt-1">Purchase virtual numbers to track calls and SMS</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Canadian regulatory bundle live status (renders only when a bundle exists) */}
      <CaBundleStatusCard API={API} getAuthConfig={getAuthConfig} isDark={isDark} />

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={(open) => { setTransferDialogOpen(open); if (!open) resetTransferDialog(); }}>
        <DialogContent className="bg-background border-border max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground">Transfer Your Phone Number</DialogTitle>
            <p className="text-muted-foreground text-sm">Port your existing number to Fieldz AI</p>
          </DialogHeader>
          <TransferDialogContent
            transferStep={transferStep} setTransferStep={setTransferStep}
            transferPhone={transferPhone} setTransferPhone={setTransferPhone}
            checkingPortability={checkingPortability} portabilityResult={portabilityResult}
            checkPortability={checkPortability}
            transferForm={transferForm} setTransferForm={setTransferForm}
            submittingTransfer={submittingTransfer} submitTransferRequest={submitTransferRequest}
            registerExistingNumber={registerExistingNumber}
            setTransferDialogOpen={setTransferDialogOpen}
            moveConfirmed={moveConfirmed} setMoveConfirmed={setMoveConfirmed}
            moveStatus={moveStatus} setMoveStatus={setMoveStatus} moveTwilioNumber={moveTwilioNumber}
            moveErrorDetail={moveErrorDetail} setMoveErrorDetail={setMoveErrorDetail}
          />
        </DialogContent>
      </Dialog>

      {/* Pending Transfer Requests */}
      {transferRequests.length > 0 && (
        <Card className="glass-card border-theme mt-4" data-testid="transfer-requests-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-foreground font-['Manrope'] text-base">Port-In Requests</CardTitle>
                <CardDescription className="text-muted-foreground">Track your phone number transfers</CardDescription>
              </div>
              <Badge className="bg-theme-hover text-muted-foreground">
                {transferRequests.filter(r => !["completed", "cancelled"].includes(r.status)).length} active
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transferRequests.map((req) => (
                <TransferRequestCard
                  key={req.request_id}
                  req={req}
                  expanded={expandedRequest === req.request_id}
                  onToggle={() => setExpandedRequest(expandedRequest === req.request_id ? null : req.request_id)}
                  onRefresh={() => refreshTransferRequest(req.request_id)}
                  onCancel={() => cancelTransferRequest(req.request_id)}
                  refreshing={refreshingRequest === req.request_id}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phone Config Modal */}
      <PhoneConfigModal
        open={phoneConfigOpen}
        onOpenChange={(open) => { setPhoneConfigOpen(open); if (!open) setEditingPhone(null); }}
        phone={editingPhone}
        jobSources={jobSources}
        onSaved={fetchAllSettings}
      />

      {/* Conversation Maintenance */}
      <ConversationMaintenanceCard API={API} getAuthConfig={getAuthConfig} />

      {/* Account-wide voice setting for IVR / voicemail / hold messages */}
      <div className="mt-4">
        <VoiceSettingsCard API={API} getAuthConfig={getAuthConfig} />
      </div>

      {/* In-App Calling (Call Routing) */}
      <CallRoutingCard API={API} getAuthConfig={getAuthConfig} />
    </>
  );
};

/* ===== Sub-components ===== */

const PhoneNumberCard = ({ phone, index, isDark, onSettings, onDelete }) => {
  const flowType = phone.call_flow?.type || "forward";
  const flowColors = { forward: { bg: "bg-blue-500/10", text: "text-blue-500", label: "Forward" }, ivr: { bg: "bg-purple-500/10", text: "text-purple-500", label: "IVR Menu" }, round_robin: { bg: "bg-orange-500/10", text: "text-orange-500", label: "Round Robin" }, time_based: { bg: "bg-cyan-500/10", text: "text-cyan-500", label: "Time-Based" }, in_app: { bg: "bg-emerald-500/10", text: "text-emerald-500", label: "In App Phone" } };
  const fc = flowColors[flowType] || flowColors.forward;

  return (
    <div className={`rounded-xl border transition-all hover:shadow-sm ${isDark ? 'bg-white/[0.02] border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-testid={`phone-item-${index}`}>
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center ${phone.is_active ? 'bg-green-500/15' : isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <PhoneCall className={`w-5 h-5 ${phone.is_active ? 'text-green-500' : 'text-muted-foreground'}`} />
            </div>
            <div className="min-w-0">
              <h4 className="font-mono text-base font-semibold text-foreground tracking-tight">{phone.phone_number}</h4>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {phone.label && <span className="text-sm text-muted-foreground">{phone.label}</span>}
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${isDark ? 'border-white/15' : 'border-gray-300'}`}>
                  {phone.purpose === "job_source" ? "Job Source" : phone.purpose === "dispatcher" ? "Dispatcher" : phone.purpose === "call_masking" ? "Call Masking" : "Main Number"}
                </Badge>
                {phone.twilio_issue === "not_owned" && (
                  <Badge className="text-[10px] font-medium px-1.5 py-0 h-4 bg-red-500/15 text-red-500 border-red-500/30" variant="outline" data-testid={`not-owned-badge-${index}`}>
                    <AlertTriangle className="w-3 h-3 mr-1" />Not on Twilio — calls will fail
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {phone.whatsapp_eligible && (
              <Badge className="text-[10px] font-medium px-2 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" variant="outline">
                <MessageCircle className="w-3 h-3 mr-1" />WhatsApp
              </Badge>
            )}
            <Badge className={`text-[10px] font-medium px-2 ${phone.is_active ? "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20" : "bg-gray-500/15 text-gray-500 border-gray-500/20"}`} variant="outline">
              {phone.is_active ? "Active" : "Inactive"}
            </Badge>
            <Button variant="ghost" size="icon" onClick={onSettings} className="h-8 w-8 text-muted-foreground hover:text-foreground" title="Configure Call Flow" data-testid={`phone-settings-${index}`}>
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete} className="h-8 w-8 text-red-400 hover:text-red-500 hover:bg-red-500/10" data-testid={`phone-delete-${index}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className={`px-4 py-2.5 border-t ${isDark ? 'border-white/[0.06] bg-white/[0.01]' : 'border-gray-100 bg-gray-50/50'}`}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Routing</span>
            <Badge variant="outline" className={`text-[10px] font-medium px-1.5 py-0 h-5 ${fc.bg} ${fc.text} border-transparent`}>{fc.label}</Badge>
          </div>
          {flowType === "forward" && phone.call_flow?.forward_to && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><PhoneOutgoing className="w-3 h-3" /><span className="font-mono">{phone.call_flow.forward_to}</span></div>
          )}
          {flowType === "ivr" && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><span>{phone.call_flow.ivr_menu?.length || 0} menu options</span></div>}
          {flowType === "round_robin" && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><span>{phone.call_flow.round_robin_numbers?.length || 0} numbers in rotation</span></div>}
          {flowType === "time_based" && phone.call_flow?.business_hours && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="w-3 h-3" /><span>{phone.call_flow.business_hours.start} - {phone.call_flow.business_hours.end}</span><span className="text-muted-foreground/40">|</span><span>After hrs: {phone.call_flow.after_hours_action || "voicemail"}</span></div>
          )}
          {flowType !== "ivr" && phone.call_flow?.whisper_message && (
            <div className="flex items-center gap-1 text-xs text-[#FFCC00]/80"><Bell className="w-3 h-3" /><span className="truncate max-w-[180px]">Whisper: &quot;{phone.call_flow.whisper_message}&quot;</span></div>
          )}
          {flowType === "ivr" && phone.call_flow?.ivr_menu?.some(m => m.whisper_message) && (
            <div className="flex items-center gap-1 text-xs text-[#FFCC00]/80"><Bell className="w-3 h-3" /><span>Per-option whispers</span></div>
          )}
        </div>
      </div>
    </div>
  );
};

const CA_PROVINCES = [
  { value: "any", label: "Any province" },
  { value: "ON", label: "Ontario" },
  { value: "QC", label: "Quebec" },
  { value: "BC", label: "British Columbia" },
  { value: "AB", label: "Alberta" },
  { value: "MB", label: "Manitoba" },
  { value: "SK", label: "Saskatchewan" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland" },
  { value: "PE", label: "PEI" },
];

const PurchaseStep1 = ({ searchAreaCode, setSearchAreaCode, searchProvince, setSearchProvince, searchCountry, setSearchCountry, searchNumberType, setSearchNumberType, searchingNumbers, searchAvailableNumbers, availableNumbers, setAvailableNumbers, purchaseForm, setPurchaseForm, searchHint, setSearchHint, setPurchaseStep, isDark }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">Search for available virtual phone numbers.</p>
    <div className="flex gap-1 p-1 rounded-lg bg-muted/50" data-testid="country-tabs">
      {[{ value: "US", label: "🇺🇸 United States" }, { value: "CA", label: "🇨🇦 Canada" }].map((c) => (
        <button key={c.value} onClick={() => { setSearchCountry(c.value); setAvailableNumbers([]); setSearchHint(""); setPurchaseForm(f => ({ ...f, phone_number: "", friendly_name: "" })); setSearchAreaCode(c.value === "CA" ? "" : "415"); setSearchProvince("any"); }}
          className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${searchCountry === c.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          data-testid={`country-${c.value}`}
        >{c.label}</button>
      ))}
    </div>
    <div className="flex gap-1 p-1 rounded-lg bg-muted/50" data-testid="number-type-tabs">
      {[{ value: "all", label: "All Types" }, { value: "local", label: "Local" }, { value: "toll_free", label: "Toll-Free" }].map((t) => (
        <button key={t.value} onClick={() => { setSearchNumberType(t.value); setAvailableNumbers([]); setSearchHint(""); setPurchaseForm(f => ({ ...f, phone_number: "", friendly_name: "" })); }}
          className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${searchNumberType === t.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          data-testid={`number-type-${t.value}`}
        >{t.label}</button>
      ))}
    </div>
    <div className="flex gap-2">
      <div className="flex-1 space-y-1">
        <Label className="text-xs text-muted-foreground">{searchNumberType === "toll_free" ? "Search (optional)" : searchCountry === "CA" ? "Area Code (optional)" : "Area Code"}</Label>
        <Input value={searchAreaCode} onChange={(e) => setSearchAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))} className="input-glass h-10 text-lg font-mono tracking-wider" placeholder={searchNumberType === "toll_free" ? "888" : searchCountry === "CA" ? "437" : "415"} maxLength={3} data-testid="area-code-input" onKeyDown={(e) => e.key === "Enter" && searchAvailableNumbers()} />
        {searchCountry === "CA" && searchNumberType !== "toll_free" && (
          <p className="text-[11px] text-muted-foreground">Big-city codes (416, 604) are often sold out — pick a province or leave everything empty to browse all Canadian inventory.</p>
        )}
      </div>
      {searchCountry === "CA" && searchNumberType !== "toll_free" && (
        <div className="w-[150px] space-y-1">
          <Label className="text-xs text-muted-foreground">Province</Label>
          <Select value={searchProvince} onValueChange={(v) => { setSearchProvince(v); setAvailableNumbers([]); setSearchHint(""); }}>
            <SelectTrigger className="input-glass h-10" data-testid="ca-province-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CA_PROVINCES.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`ca-province-${p.value}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex items-end">
        <Button onClick={searchAvailableNumbers} disabled={searchingNumbers || (searchNumberType !== "toll_free" && searchCountry !== "CA" && searchAreaCode.length < 3)} className="btn-secondary h-10" data-testid="search-numbers-btn">
          {searchingNumbers ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />} Search
        </Button>
      </div>
    </div>
    {searchHint && (
      <div className={`flex items-start gap-2 p-3 rounded-lg text-xs ${isDark ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`} data-testid="search-hint">
        <Info className="w-4 h-4 mt-0.5 shrink-0" /><span>{searchHint}</span>
      </div>
    )}
    {availableNumbers.length > 0 && (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">{availableNumbers.length} Numbers Available</Label>
        <ScrollArea className="h-[220px]">
          <div className="space-y-1.5 pr-2">
            {availableNumbers.map((num, i) => {
              const isSelected = purchaseForm.phone_number === num.phone_number;
              const caps = num.capabilities || {};
              return (
                <button key={i} onClick={() => setPurchaseForm(f => ({ ...f, phone_number: num.phone_number, friendly_name: num.friendly_name || num.phone_number }))}
                  className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between group ${isSelected ? "bg-[#FFCC00]/15 border-2 border-[#FFCC00]/50 shadow-sm" : isDark ? "bg-white/[0.03] border border-white/10 hover:border-white/25" : "bg-gray-50 border border-gray-200 hover:border-gray-300"}`}
                  data-testid={`available-number-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-[#FFCC00]/20' : isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <Phone className={`w-4 h-4 ${isSelected ? 'text-[#FFCC00]' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="min-w-0">
                      <span className="font-mono text-sm font-medium text-foreground block truncate">{num.friendly_name || num.phone_number}</span>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {num.number_type && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${num.number_type === "toll_free" ? isDark ? "bg-purple-500/20 text-purple-300" : "bg-purple-100 text-purple-700" : isDark ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"}`}>{num.number_type === "toll_free" ? "Toll-Free" : "Local"}</span>}
                        {num.locality && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><span className="w-2.5 h-2.5" />{num.locality}{num.region ? `, ${num.region}` : ""}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-2">
                    <div className="hidden sm:flex items-center gap-1">
                      {caps.sms && <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${isDark ? 'bg-green-500/15 text-green-400' : 'bg-green-100 text-green-700'}`}>SMS</span>}
                      {caps.voice && <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${isDark ? 'bg-sky-500/15 text-sky-400' : 'bg-sky-100 text-sky-700'}`}>Voice</span>}
                      {caps.mms && <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${isDark ? 'bg-orange-500/15 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>MMS</span>}
                      {num.whatsapp_eligible && <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>WhatsApp</span>}
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-[#FFCC00]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    )}
    {availableNumbers.length > 0 && purchaseForm.phone_number && (
      <Button onClick={() => setPurchaseStep(2)} className="w-full btn-primary h-10" data-testid="step1-next">
        Continue with {purchaseForm.friendly_name || purchaseForm.phone_number} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    )}
  </div>
);

const PurchaseStep2 = ({ purchaseForm, setPurchaseForm, setPurchaseStep, jobSources, isDark, backStep = 1, nextStep = 3 }) => (
  <div className="space-y-4">
    <div className={`p-3 rounded-xl flex items-center gap-3 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
      <div className="w-10 h-10 rounded-lg bg-[#FFCC00]/20 flex items-center justify-center"><Phone className="w-5 h-5 text-[#FFCC00]" /></div>
      <div><p className="font-mono text-sm font-medium text-foreground">{purchaseForm.friendly_name || purchaseForm.phone_number}</p><p className="text-xs text-muted-foreground">Your new virtual number</p></div>
      <Button variant="ghost" size="sm" onClick={() => setPurchaseStep(1)} className="ml-auto text-xs text-muted-foreground h-7">Change</Button>
    </div>
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Label / Nickname</Label>
        <Input value={purchaseForm.label} onChange={(e) => setPurchaseForm({ ...purchaseForm, label: e.target.value })} className="input-glass h-9" placeholder="e.g., Main Line, Marketing, Support" data-testid="purchase-label" />
        <p className="text-[10px] text-muted-foreground/60">Give this number a name so you can easily identify it later</p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Assign To</Label>
        <Select value={purchaseForm.assignment_type} onValueChange={(v) => setPurchaseForm({ ...purchaseForm, assignment_type: v, assignment_id: "" })}>
          <SelectTrigger className="input-glass h-9" data-testid="purchase-assign-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="business"><div className="flex items-center gap-2"><Building className="w-3.5 h-3.5 text-blue-500" /><span>Business (Main Line)</span></div></SelectItem>
            <SelectItem value="job_source"><div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-green-500" /><span>Job Source (Track Marketing)</span></div></SelectItem>
          </SelectContent>
        </Select>
      </div>
      {purchaseForm.assignment_type === "job_source" && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Select Job Source</Label>
          <Select value={purchaseForm.assignment_id} onValueChange={(v) => setPurchaseForm({ ...purchaseForm, assignment_id: v })}>
            <SelectTrigger className="input-glass h-9" data-testid="purchase-source-select"><SelectValue placeholder="Choose a job source..." /></SelectTrigger>
            <SelectContent>{jobSources.map((source) => <SelectItem key={source.source_id} value={source.source_id}>{source.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
    <div className="flex gap-2 pt-1">
      <Button variant="ghost" onClick={() => setPurchaseStep(backStep)} className="text-muted-foreground">Back</Button>
      <Button onClick={() => setPurchaseStep(nextStep)} className="flex-1 btn-primary h-10" disabled={purchaseForm.assignment_type === "job_source" && !purchaseForm.assignment_id} data-testid="step2-next">
        Review & Confirm <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  </div>
);

const PurchaseStep3 = ({ purchaseForm, jobSources, setPurchaseStep, purchaseNumber, isDark, backStep = 2, caProfile = null }) => (
  <div className="space-y-4">
    <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
      <div className={`p-4 ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#FFCC00]/20 flex items-center justify-center"><Phone className="w-6 h-6 text-[#FFCC00]" /></div>
          <div><p className="font-mono text-lg font-bold text-foreground">{purchaseForm.friendly_name || purchaseForm.phone_number}</p><p className="text-sm text-muted-foreground">{purchaseForm.label || "No label"}</p></div>
        </div>
      </div>
      <div className="p-4 space-y-2.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Assignment</span>
          <span className="text-foreground font-medium flex items-center gap-1.5">
            {purchaseForm.assignment_type === "business" ? <><Building className="w-3.5 h-3.5 text-blue-500" /> Business Line</> : <><Briefcase className="w-3.5 h-3.5 text-green-500" /> Job Source</>}
          </span>
        </div>
        {purchaseForm.assignment_type === "job_source" && purchaseForm.assignment_id && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Source</span>
            <span className="text-foreground font-medium">{jobSources.find(s => s.source_id === purchaseForm.assignment_id)?.name || purchaseForm.assignment_id}</span>
          </div>
        )}
        {caProfile && (
          <div className="flex items-center justify-between text-sm" data-testid="confirm-ca-compliance-row">
            <span className="text-muted-foreground">Regulatory Info</span>
            <span className="text-foreground font-medium flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {caProfile.legal_business_name}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Capabilities</span><span className="text-foreground font-medium">Voice + SMS</span></div>
        <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}><p className="text-xs text-muted-foreground">After purchase, you can configure call routing, IVR menus, and whisper messages.</p></div>
      </div>
    </div>
    <div className="flex gap-2">
      <Button variant="ghost" onClick={() => setPurchaseStep(backStep)} className="text-muted-foreground">Back</Button>
      <Button onClick={() => purchaseNumber({ phone_number: purchaseForm.phone_number, label: purchaseForm.label || "Main Line", assignment_type: purchaseForm.assignment_type, assignment_id: purchaseForm.assignment_id })} className="flex-1 btn-primary h-10" data-testid="confirm-purchase-btn">
        <Check className="w-4 h-4 mr-2" /> Purchase Number
      </Button>
    </div>
  </div>
);

const TransferDialogContent = ({ transferStep, setTransferStep, transferPhone, setTransferPhone, checkingPortability, portabilityResult, checkPortability, transferForm, setTransferForm, submittingTransfer, submitTransferRequest, registerExistingNumber, setTransferDialogOpen, moveConfirmed, setMoveConfirmed, moveStatus, setMoveStatus, moveTwilioNumber, moveErrorDetail, setMoveErrorDetail }) => (
  <div className="space-y-4 overflow-y-auto flex-1 pr-1">
    {transferStep === "check" && (
      <>
        <div className="space-y-2">
          <Label className="text-muted-foreground">Phone Number to Transfer</Label>
          <Input value={transferPhone} onChange={(e) => setTransferPhone(formatPhoneInput(e.target.value))} className="input-glass" placeholder="(555) 123-4567" data-testid="transfer-phone-input" />
          <p className="text-muted-foreground/60 text-xs">Enter the number you want to transfer from your current carrier</p>
        </div>
        {portabilityResult && !portabilityResult.portable && !portabilityResult.already_in_account && !portabilityResult.is_twilio_number && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 text-sm font-medium">This number cannot be transferred via porting</p>
            <p className="text-red-400/70 text-xs mt-1">{portabilityResult.not_portable_reason || "The number is not eligible for porting."}</p>
            <button onClick={() => { setTransferStep("twilio_move"); setMoveStatus(null); }} className="text-blue-400 text-xs mt-2 underline hover:text-blue-300 transition-colors" data-testid="manual-twilio-move-link">
              Already on another Twilio account? Move it instead
            </button>
          </div>
        )}
        {portabilityResult && portabilityResult.already_in_account && (
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-blue-400 text-sm font-medium flex items-center gap-2"><Check className="w-4 h-4" /> Number found in your Twilio account</p>
            <p className="text-blue-400/70 text-xs mt-1">This number is already in your Twilio account. You can connect it directly — no porting needed!</p>
          </div>
        )}
        <Button onClick={checkPortability} disabled={!transferPhone.trim() || checkingPortability} className="w-full btn-primary" data-testid="check-portability-btn">
          {checkingPortability ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Check Availability
        </Button>
      </>
    )}

    {/* ─── TWILIO MOVE FLOW ─── */}
    {transferStep === "twilio_move" && (
      <>
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-blue-400 text-sm font-bold flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" /> Move Your Twilio Number
          </p>
          <p className="text-blue-400/70 text-xs mt-1">
            This number is already hosted on Twilio. Provide the source account credentials to move it to Fieldz AI.
          </p>
        </div>

        <div className="p-3 rounded-lg bg-theme-surface border border-theme">
          <p className="text-foreground text-sm font-medium">{portabilityResult?.phone_number || transferPhone}</p>
          <p className="text-muted-foreground text-xs mt-1">
            This is a Twilio account transfer, not a carrier port. It is usually instant.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-muted-foreground">Source Account SID *</Label>
            <Input value={transferForm.account_number} onChange={(e) => setTransferForm({ ...transferForm, account_number: e.target.value })} className="input-glass font-mono text-xs" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="twilio-move-account-sid" />
            <p className="text-muted-foreground/60 text-xs">The Twilio Account SID where this number currently lives</p>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">PIN / Auth Token *</Label>
            <Input type="password" value={transferForm.account_pin} onChange={(e) => setTransferForm({ ...transferForm, account_pin: e.target.value })} className="input-glass font-mono text-xs" placeholder="PIN or Auth Token from source account" data-testid="twilio-move-pin" />
            <p className="text-muted-foreground/60 text-xs">The port-out PIN or Auth Token provided by the source account owner</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-muted-foreground">Label for this number</Label>
          <Input value={transferForm.label} onChange={(e) => setTransferForm({ ...transferForm, label: e.target.value })} className="input-glass" placeholder="e.g., Main Line, Support" data-testid="twilio-move-label-input" />
        </div>

        <label className="flex items-start gap-3 p-3 rounded-lg bg-theme-surface border border-theme cursor-pointer hover:border-blue-500/30 transition-colors" data-testid="twilio-move-confirm-checkbox">
          <input type="checkbox" checked={moveConfirmed} onChange={(e) => setMoveConfirmed(e.target.checked)} className="mt-0.5 accent-blue-500 w-4 h-4" />
          <span className="text-sm text-muted-foreground leading-snug">I confirm I own or am authorized to transfer this number from the source Twilio account to Fieldz AI</span>
        </label>

        {moveStatus === "moving" && <MoveStatusBar status="moving" text="Connecting to source Twilio account..." />}
        {moveStatus === "configuring" && <MoveStatusBar status="configuring" text="Moving number and applying configuration..." />}
        {moveStatus === "completed" && <MoveStatusBar status="completed" text="Your Twilio number was moved successfully and connected to this Fieldz AI workspace." />}
        {moveStatus === "failed" && (
          <div className="space-y-3">
            <MoveStatusBar status="failed" text={moveErrorDetail || "Failed to move number."} />
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-amber-400 text-sm font-medium">The instant move didn&apos;t work for this number.</p>
              <p className="text-amber-400/70 text-xs mt-1">
                This usually means the source account is outside the Fieldz AI Twilio organization. 
                You can still transfer this number using a standard Port-In request with the Account SID and PIN.
              </p>
              <Button
                size="sm"
                className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                onClick={() => {
                  setMoveStatus(null);
                  setMoveErrorDetail("");
                  // Pre-fill porting form with existing data and switch to port-in step
                  setTransferForm(f => ({ ...f, current_carrier: "Twilio" }));
                  setTransferStep("form");
                }}
                data-testid="try-portin-btn"
              >
                <ArrowRight className="w-3 h-3 mr-1" /> Try Port-In Instead
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="ghost" onClick={() => { setTransferStep("check"); setMoveStatus(null); }} className="text-muted-foreground" disabled={moveStatus === "moving" || moveStatus === "configuring"}>Back</Button>
          <Button onClick={moveTwilioNumber} disabled={!moveConfirmed || !transferForm.account_number?.trim() || !transferForm.account_pin?.trim() || moveStatus === "moving" || moveStatus === "configuring" || moveStatus === "completed"} className="btn-primary" data-testid="move-twilio-btn">
            {(moveStatus === "moving" || moveStatus === "configuring") ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />} Move Number
          </Button>
        </div>
      </>
    )}

    {transferStep === "connect" && (
      <>
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-blue-400 text-sm font-medium flex items-center gap-2"><Check className="w-4 h-4" /> {portabilityResult?.phone_number || transferPhone}</p>
          <p className="text-blue-400/70 text-xs mt-1">This number is already in your Twilio account. We&apos;ll configure it to work with Fieldz AI.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-muted-foreground">Label for this number</Label>
          <Input value={transferForm.label} onChange={(e) => setTransferForm({ ...transferForm, label: e.target.value })} className="input-glass" placeholder="e.g., Main Line, Support" data-testid="connect-label-input" />
        </div>
        <div className="p-3 rounded-lg bg-[#FFCC00]/10 border border-[#FFCC00]/20">
          <p className="text-[#FFCC00] text-xs font-medium flex items-center gap-1"><Info className="w-3 h-3" /> What happens next</p>
          <p className="text-muted-foreground text-xs mt-1">We&apos;ll update the number&apos;s call and SMS webhooks to route through Fieldz AI. This is instant — no waiting period.</p>
        </div>
        <div className="flex justify-end gap-3 pt-1">
          <Button variant="ghost" onClick={() => setTransferStep("check")} className="text-muted-foreground">Back</Button>
          <Button onClick={registerExistingNumber} disabled={submittingTransfer} className="btn-primary" data-testid="connect-number-btn">
            {submittingTransfer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />} Connect Number
          </Button>
        </div>
      </>
    )}
    {transferStep === "form" && (
      <>
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20"><p className="text-green-400 text-sm font-medium flex items-center gap-2"><Check className="w-4 h-4" /> {transferPhone} is eligible for transfer</p></div>

        {/* Carrier Info */}
        <div className="space-y-2"><Label className="text-muted-foreground">Current Carrier</Label><Input value={transferForm.current_carrier} onChange={(e) => setTransferForm({ ...transferForm, current_carrier: e.target.value })} className="input-glass" placeholder="e.g., AT&T, Verizon" data-testid="transfer-carrier-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label className="text-muted-foreground">Account Number</Label><Input value={transferForm.account_number} onChange={(e) => setTransferForm({ ...transferForm, account_number: e.target.value })} className="input-glass" placeholder="From your carrier" data-testid="transfer-account-input" /></div>
          <div className="space-y-2"><Label className="text-muted-foreground">Security PIN</Label><Input type="password" value={transferForm.account_pin} onChange={(e) => setTransferForm({ ...transferForm, account_pin: e.target.value })} className="input-glass" placeholder="PIN / Passcode" data-testid="transfer-pin-input" /></div>
        </div>

        {/* Authorized Representative */}
        <div className="space-y-2"><Label className="text-muted-foreground">Authorized Name *</Label><Input value={transferForm.authorized_name} onChange={(e) => setTransferForm({ ...transferForm, authorized_name: e.target.value })} className="input-glass" placeholder="Name on the account" data-testid="transfer-name-input" /><p className="text-muted-foreground/60 text-xs">Must match the name on your current carrier account</p></div>
        <div className="space-y-2"><Label className="text-muted-foreground">Contact Email *</Label><Input type="email" value={transferForm.contact_email} onChange={(e) => setTransferForm({ ...transferForm, contact_email: e.target.value })} className="input-glass" placeholder="email@example.com" data-testid="transfer-email-input" /><p className="text-muted-foreground/60 text-xs">Used for porting communications and LOA authorization</p></div>

        {/* Billing Address */}
        <div className="space-y-2">
          <Label className="text-muted-foreground">Billing Address</Label>
          <Input value={transferForm.billing_street} onChange={(e) => setTransferForm({ ...transferForm, billing_street: e.target.value })} className="input-glass" placeholder="Street address" data-testid="transfer-street-input" />
          <div className="grid grid-cols-3 gap-2">
            <Input value={transferForm.billing_city} onChange={(e) => setTransferForm({ ...transferForm, billing_city: e.target.value })} className="input-glass" placeholder="City" data-testid="transfer-city-input" />
            <Input value={transferForm.billing_state} onChange={(e) => setTransferForm({ ...transferForm, billing_state: e.target.value })} className="input-glass" placeholder="State" data-testid="transfer-state-input" />
            <Input value={transferForm.billing_zip} onChange={(e) => setTransferForm({ ...transferForm, billing_zip: e.target.value })} className="input-glass" placeholder="ZIP" data-testid="transfer-zip-input" />
          </div>
          <p className="text-muted-foreground/60 text-xs">Must match the billing address on your carrier account</p>
        </div>

        <div className="space-y-2"><Label className="text-muted-foreground">Label for this number</Label><Input value={transferForm.label} onChange={(e) => setTransferForm({ ...transferForm, label: e.target.value })} className="input-glass" placeholder="e.g., Main Line, Support" /></div>

        {/* LOA Notice */}
        <div className="p-3 rounded-lg bg-[#FFCC00]/10 border border-[#FFCC00]/20">
          <p className="text-[#FFCC00] text-xs font-medium flex items-center gap-1"><Info className="w-3 h-3" /> Important</p>
          <p className="text-muted-foreground text-xs mt-1">Phone number transfers typically take 1-3 weeks. A Letter of Authorization (LOA) may be required — we&apos;ll contact you at the email above if needed.</p>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="ghost" onClick={() => setTransferStep("check")} className="text-muted-foreground">Back</Button>
          <Button onClick={submitTransferRequest} disabled={submittingTransfer || !(transferForm.authorized_name || "").trim() || !(transferForm.contact_email || "").trim()} className="btn-primary" data-testid="submit-transfer-btn">
            {submittingTransfer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />} Submit Transfer Request
          </Button>
        </div>
      </>
    )}
    {transferStep === "submitted" && (
      <div className="text-center py-4 space-y-3">
        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto"><Check className="w-6 h-6 text-green-400" /></div>
        <h3 className="text-foreground font-medium">Transfer Request Submitted</h3>
        <p className="text-muted-foreground text-sm">We&apos;ve received your request to transfer {transferPhone}.</p>
        <Button onClick={() => setTransferDialogOpen(false)} className="btn-primary">Done</Button>
      </div>
    )}
  </div>
);

const PORT_STATUS_CONFIG = {
  pending: { bg: "bg-gray-500/15", text: "text-gray-400", label: "Pending", icon: Clock, description: "Request saved locally, not yet submitted to Twilio." },
  pending_review: { bg: "bg-orange-500/15", text: "text-orange-400", label: "Pending Review", icon: Eye, description: "Awaiting manual review by the Fieldz AI team." },
  submitted: { bg: "bg-blue-500/15", text: "text-blue-400", label: "Submitted", icon: FileCheck, description: "Submitted to Twilio. Under initial processing." },
  in_review: { bg: "bg-indigo-500/15", text: "text-indigo-400", label: "In Review", icon: Eye, description: "Twilio is reviewing the port-in request with the carrier." },
  waiting_signature: { bg: "bg-purple-500/15", text: "text-purple-400", label: "Awaiting Signature", icon: FileCheck, description: "A Letter of Authorization (LOA) has been sent for your signature." },
  in_progress: { bg: "bg-yellow-500/15", text: "text-yellow-400", label: "In Progress", icon: Loader2, description: "Port request submitted to the losing carrier. Transfer is underway." },
  action_required: { bg: "bg-red-500/15", text: "text-red-400", label: "Action Required", icon: AlertTriangle, description: "The carrier rejected or flagged this request. Check details and resubmit if needed." },
  completed: { bg: "bg-green-500/15", text: "text-green-400", label: "Completed", icon: Check, description: "Number has been successfully ported to your account." },
  canceling: { bg: "bg-orange-500/15", text: "text-orange-400", label: "Canceling", icon: X, description: "Cancellation is being processed by Twilio." },
  cancelled: { bg: "bg-gray-500/15", text: "text-gray-500", label: "Cancelled", icon: X, description: "This port request was cancelled." },
};

const TransferRequestCard = ({ req, expanded, onToggle, onRefresh, onCancel, refreshing }) => {
  const config = PORT_STATUS_CONFIG[req.status] || PORT_STATUS_CONFIG.pending;
  const StatusIcon = config.icon;
  const isTerminal = ["completed", "cancelled"].includes(req.status);
  const isActionRequired = req.status === "action_required";
  const hasTimeline = req.status_history && req.status_history.length > 0;

  return (
    <div
      className={`rounded-xl border transition-all ${isActionRequired ? "border-red-500/30 bg-red-500/[0.03]" : "bg-theme-surface border-theme"}`}
      data-testid={`transfer-request-${req.request_id}`}
    >
      {/* Main row */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${config.bg}`}>
              <StatusIcon className={`w-5 h-5 ${config.text} ${req.status === "in_progress" ? "animate-spin" : ""}`} />
            </div>
            <div className="min-w-0">
              <h4 className="font-mono text-sm font-semibold text-foreground">{req.phone_number}</h4>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {req.current_carrier && <span className="text-xs text-muted-foreground">{req.current_carrier}</span>}
                <Badge className={`text-[10px] font-medium px-2 border-transparent ${config.bg} ${config.text}`}>
                  {config.label}
                </Badge>
                {req.twilio_port_sid && (
                  <span className="text-[10px] text-muted-foreground/50 font-mono hidden sm:inline">{req.twilio_port_sid}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!isTerminal && req.twilio_port_sid && (
              <Button variant="ghost" size="icon" onClick={onRefresh} disabled={refreshing} className="h-8 w-8 text-muted-foreground hover:text-foreground" title="Refresh status from Twilio" data-testid={`refresh-transfer-${req.request_id}`}>
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onToggle} className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid={`toggle-transfer-${req.request_id}`}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            {!isTerminal && (
              <Button variant="ghost" size="icon" onClick={onCancel} className="h-8 w-8 text-red-400 hover:text-red-500 hover:bg-red-500/10" title="Cancel request" data-testid={`cancel-transfer-${req.request_id}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Status description + estimated date (always visible) */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <p className="text-xs text-muted-foreground">{config.description}</p>
          {req.target_port_in_date && (
            <div className="flex items-center gap-1.5 shrink-0">
              <CalendarClock className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs font-medium text-blue-400">
                Est. {new Date(req.target_port_in_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            </div>
          )}
        </div>

        {/* Action required alert */}
        {isActionRequired && (
          <div className="mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-400 font-medium flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Action needed — the carrier flagged this request. Contact support or resubmit with corrected details.
            </p>
          </div>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-theme pt-3 space-y-4">
          {/* Info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Label</span>
              <p className="text-sm text-foreground mt-0.5">{req.label || "—"}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Authorized Name</span>
              <p className="text-sm text-foreground mt-0.5">{req.authorized_name || "—"}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Submitted</span>
              <p className="text-sm text-foreground mt-0.5">
                {req.created_at ? new Date(req.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
              </p>
            </div>
            {req.twilio_status && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Twilio Status</span>
                <p className="text-sm text-foreground mt-0.5">{req.twilio_status}</p>
              </div>
            )}
            {req.target_port_in_date && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Target Port Date</span>
                <p className="text-sm text-foreground mt-0.5">
                  {new Date(req.target_port_in_date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
            )}
            {req.account_number && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Account #</span>
                <p className="text-sm text-foreground mt-0.5 font-mono">{"•".repeat(Math.max(0, req.account_number.length - 4))}{req.account_number.slice(-4)}</p>
              </div>
            )}
          </div>

          {/* Status Timeline */}
          {hasTimeline && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Status History</span>
              <div className="mt-2 space-y-0">
                {req.status_history.map((entry, idx) => {
                  const entryConfig = PORT_STATUS_CONFIG[entry.status] || PORT_STATUS_CONFIG.pending;
                  const isLast = idx === req.status_history.length - 1;
                  return (
                    <div key={idx} className="flex gap-3 relative" data-testid={`timeline-entry-${idx}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${isLast ? entryConfig.bg.replace("/15", "/60") : "bg-muted-foreground/20"}`} />
                        {!isLast && <div className="w-px flex-1 bg-muted-foreground/10 my-0.5" />}
                      </div>
                      <div className={`pb-3 ${isLast ? "" : ""}`}>
                        <p className={`text-xs font-medium ${isLast ? entryConfig.text : "text-muted-foreground"}`}>{entryConfig.label}</p>
                        {entry.note && <p className="text-[11px] text-muted-foreground/70 mt-0.5">{entry.note}</p>}
                        <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MoveStatusBar = ({ status, text }) => {
  const styles = {
    moving: "bg-blue-500/10 border-blue-500/20 text-blue-400",
    configuring: "bg-amber-500/10 border-amber-500/20 text-amber-400",
    completed: "bg-green-500/10 border-green-500/20 text-green-400",
    failed: "bg-red-500/10 border-red-500/20 text-red-400",
  };
  const icons = {
    moving: <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />,
    configuring: <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />,
    completed: <Check className="w-4 h-4 flex-shrink-0" />,
    failed: <AlertCircle className="w-4 h-4 flex-shrink-0" />,
  };
  return (
    <div className={`p-3 rounded-lg border ${styles[status]}`} data-testid={`move-status-${status}`}>
      <p className="text-sm font-medium flex items-center gap-2">{icons[status]} {text}</p>
    </div>
  );
};

export default PhoneManagementTab;
